import React, { useState, useEffect } from 'react';
import { Plus, Edit, AlertCircle, ChevronDown, Save, X, Building, MapPin, Server, Tag, RefreshCw, Clock } from 'lucide-react';
import { api } from '../services/api';
import { DEFAULT_TIME_ZONE, formatTimeZoneLabel, resolveTimeZone, getTimeZoneColor } from '../services/dateService';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import { AdminAddButton } from './admin/AdminAddButton';
import { useAdminToast } from './admin/AdminToast';
import { useConfirmDialog } from './admin/ConfirmDialog';
import { AdminActionButton, AdminRowActions } from './admin/AdminActionButton';

const TIME_ZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern — New York (auto DST)' },
  { value: 'America/Chicago', label: 'Central — Chicago (auto DST)' },
  { value: 'America/Denver', label: 'Mountain — Denver (auto DST)' },
  { value: 'America/Phoenix', label: 'Mountain — Phoenix (MST, no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific — Los Angeles (auto DST)' },
  { value: 'America/Tijuana', label: 'Pacific — Tijuana (auto DST)' },
  { value: 'America/Vancouver', label: 'Pacific — Vancouver (auto DST)' },
  { value: 'America/Whitehorse', label: 'Yukon — Whitehorse (MST, no DST)' },
  { value: 'America/Dawson', label: 'Yukon — Dawson (MST, no DST)' }
];

const OfficeMappingManagement = () => {
  const [mappings, setMappings] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [enterprises, setEnterprises] = useState([]);
  const [bocs, setBocs] = useState([]);
  const [stores, setStores] = useState([]);
  const [localStores, setLocalStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { showSuccess, toastElement } = useAdminToast();
  const { confirm, confirmDialog } = useConfirmDialog();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  
  const [formData, setFormData] = useState({
    nodeIdFk: '',
    officeMasterId: '',
    storeId: '',
    storeName: '',
    bocIdFk: '',
    enterpriseIdFk: '',
    remoteStoreIdFk: '',
    remoteBocIdFk: '',
    remoteEnterpriseIdFk: '',
    dealerCode: '',
    officeCode: '',
    timeZone: DEFAULT_TIME_ZONE,
    n2CategoryId: '',
  });

  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [selectedBoc, setSelectedBoc] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [creationMode, setCreationMode] = useState('lisa-backend'); // 'lisa-backend' or 'internal-db'
  
  const [internalEnterprises, setInternalEnterprises] = useState([]);
  const [internalBOCs, setInternalBOCs] = useState([]);
  const [internalStores, setInternalStores] = useState([]);
  const [selectedInternalEnterprise, setSelectedInternalEnterprise] = useState(null);
  const [selectedInternalBOC, setSelectedInternalBOC] = useState(null);
  const [selectedInternalStore, setSelectedInternalStore] = useState(null);
  
  const [loadingBOCs, setLoadingBOCs] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);
  const [loadingInternalBOCs, setLoadingInternalBOCs] = useState(false);
  const [loadingInternalStores, setLoadingInternalStores] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [syncingId, setSyncingId] = useState(null);

  useEffect(() => {
    loadMappings();
    loadLocalStores();
    loadNodes();
    loadCategories();
  }, []);

  // Effect to select store when stores are loaded during edit
  useEffect(() => {
    if (editingMapping && editingMapping.remoteStoreIdFk && stores.length > 0 && selectedBoc) {
      const foundStore = stores.find(s => String(s.storeIdPk) === String(editingMapping.remoteStoreIdFk));
      if (foundStore && selectedStore !== foundStore.storeIdPk) {
        setSelectedStore(foundStore.storeIdPk);
      } else if (!foundStore) {
        console.error('Store not found in BOC stores list:', {
          remoteStoreIdFk: editingMapping.remoteStoreIdFk,
          availableStores: stores.map(s => ({ storeId: s.storeId, storeIdPk: s.storeIdPk, name: s.name }))
        });
      }
    }
  }, [stores, selectedBoc, editingMapping]);

  const loadMappings = async () => {
    setError('');
    try {
      const response = await api.get('/admin/office-mapping');
      setMappings(response.data);
    } catch (error) {
      console.error('Error loading office mappings:', error);
      setError('Failed to load office mappings');
    } finally {
      setLoading(false);
    }
  };

  const loadNodes = async () => {
    setError('');
    try {
      const response = await api.get('/admin/nodes/active');
      setNodes(response.data);
    } catch (error) {
      console.error('Error loading nodes:', error);
      setError('Failed to load nodes');
    }
  };

  const loadLocalStores = async () => {
    setError('');
    try {
      const response = await api.get('/admin/stores');
      setLocalStores(response.data);
    } catch (error) {
      console.error('Error loading local stores:', error);
    }
  };

  const loadCategories = async () => {
    setError('');
    try {
      const response = await api.get('/admin/office-mapping/local/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadEnterprises = async (nodeId) => {
    const targetNodeId = nodeId || formData.nodeIdFk;
    if (!targetNodeId) {
      setError('Please select a node first');
      return;
    }
    setError('');
    try {
      const response = await api.get(`/admin/office-mapping/lisa/enterprises?nodeId=${targetNodeId}`);
      setEnterprises(response.data);
    } catch (error) {
      console.error('Error loading enterprises:', error);
      setError('Failed to load enterprises from Lisa Backend');
    }
  };

  const loadBocsByEnterprise = async (enterpriseIdPk) => {
    setLoadingBOCs(true);
    setError('');
    try {
      const response = await api.get(`/admin/office-mapping/lisa/enterprises/${enterpriseIdPk}/bocs?nodeId=${formData.nodeIdFk}`);
      setBocs(response.data);
    } catch (error) {
      console.error('Error loading BOCs:', error);
      setError('Failed to load BOCs from Lisa Backend');
    } finally {
      setLoadingBOCs(false);
    }
  };

  const loadStoresByBoc = async (bocIdPk) => {
    setLoadingStores(true);
    setError('');
    try {
      const response = await api.get(`/admin/office-mapping/lisa/bocs/${bocIdPk}/stores?nodeId=${formData.nodeIdFk}`);
      setStores(response.data);
    } catch (error) {
      console.error('Error loading stores:', error);
      setError('Failed to load stores from Lisa Backend');
    } finally {
      setLoadingStores(false);
    }
  };

  const loadInternalEnterprises = async () => {
    setError('');
    try {
      const response = await api.get('/admin/enterprises');
      setInternalEnterprises(response.data);
    } catch (error) {
      console.error('Error loading internal enterprises:', error);
      setError('Failed to load internal enterprises');
    }
  };

  const loadInternalBOCs = async (enterpriseId) => {
    setLoadingInternalBOCs(true);
    setError('');
    try {
      const response = await api.get(`/admin/bocs/enterprise/${enterpriseId}`);
      setInternalBOCs(response.data);
    } catch (error) {
      console.error('Error loading internal BOCs:', error);
      setError('Failed to load internal BOCs');
    } finally {
      setLoadingInternalBOCs(false);
    }
  };

  const loadInternalStores = async (bocId) => {
    setLoadingInternalStores(true);
    setError('');
    try {
      const response = await api.get(`/admin/stores/boc/${bocId}`);
      setInternalStores(response.data);
    } catch (error) {
      console.error('Error loading internal stores:', error);
      setError('Failed to load internal stores');
    } finally {
      setLoadingInternalStores(false);
    }
  };


  const handleEnterpriseChange = (enterpriseIdPk) => {
    setSelectedEnterprise(enterpriseIdPk);
    setSelectedBoc(null);
    setSelectedStore(null);
    setBocs([]);
    setStores([]);
    setFormData({ ...formData, remoteEnterpriseIdFk: enterpriseIdPk });
    if (enterpriseIdPk) {
      loadBocsByEnterprise(enterpriseIdPk);
    }
  };

  const handleBocChange = (bocIdPk) => {
    setSelectedBoc(bocIdPk);
    setSelectedStore(null);
    setStores([]);
    if (bocIdPk) {
      loadStoresByBoc(bocIdPk);
    }
  };

  const handleStoreChange = (storeIdPk, storeData) => {
    setSelectedStore(storeIdPk);
    if (storeData) {
      setFormData({
        ...formData,
        storeId: storeData.storeId || '',
        storeName: storeData.name || '',
        bocIdFk: selectedBoc,
        enterpriseIdFk: selectedEnterprise,
        remoteStoreIdFk: storeIdPk,
        remoteEnterpriseIdFk: selectedEnterprise,
        dealerCode: storeData.dealerCode || '',
        officeMasterId: storeData.officeMasterId || '',
        timeZone: resolveTimeZone(storeData.timeZone),
      });
    }
  };

  const handleInternalEnterpriseChange = (enterpriseId) => {
    setSelectedInternalEnterprise(enterpriseId);
    setSelectedInternalBOC(null);
    setSelectedInternalStore(null);
    setInternalBOCs([]);
    setInternalStores([]);
    setFormData({ ...formData, enterpriseIdFk: enterpriseId });
    if (enterpriseId) {
      loadInternalBOCs(enterpriseId);
    }
  };

  const handleInternalBOCChange = (bocId) => {
    setSelectedInternalBOC(bocId);
    setSelectedInternalStore(null);
    setInternalStores([]);
    setFormData({ ...formData, bocIdFk: bocId });
    if (bocId) {
      loadInternalStores(bocId);
    }
  };

  const handleInternalStoreChange = (storeId) => {
    setSelectedInternalStore(storeId);
    setFormData({ ...formData, storeIdFk: storeId });
  };


  const handleCreateMapping = async (e) => {
    e.preventDefault();
    
    // Check if remoteStoreIdFk already exists
    const existingMapping = mappings.find(m => m.remoteStoreIdFk === formData.remoteStoreIdFk);
    if (existingMapping) {
      setError('A mapping with this remote store ID already exists');
      return;
    }
    
    setError('');
    setSubmitting(true);
    try {
      let requestData;
      
      if (creationMode === 'lisa-backend') {
        // Create from Lisa Backend - create Enterprise + BOC + Store + OfficeMapping
        const selectedEnterpriseData = enterprises.find(e => e.enterpriseIdPk === selectedEnterprise);
        const selectedBOCData = bocs.find(b => b.bocIdPk === selectedBoc);
        const selectedStoreData = stores.find(s => s.storeIdPk === selectedStore);
        
        requestData = {
          createFromLisaBackend: true,
          nodeIdFk: formData.nodeIdFk,
          officeMasterId: formData.officeMasterId,
          enterpriseName: selectedEnterpriseData?.name,
          enterpriseDescription: selectedEnterpriseData?.description,
          bocId: selectedBOCData?.bocId,
          bocName: selectedBOCData?.name,
          bocDescription: selectedBOCData?.description,
          storeId: selectedStoreData?.storeId,
          storeName: selectedStoreData?.name,
          remoteStoreIdFk: formData.remoteStoreIdFk,
          remoteBocIdFk: selectedBOCData?.bocIdPk,
          remoteEnterpriseIdFk: formData.remoteEnterpriseIdFk,
          dealerCode: formData.dealerCode,
          officeCode: formData.officeCode,
          timeZone: formData.timeZone || null,
          companyId: selectedBOCData?.bocId,
          n2CategoryId: formData.n2CategoryId ? Number(formData.n2CategoryId) : null,
        };
      } else {
        // Use Internal DB - select existing store and map to Lisa Backend
        const selectedInternalBOCData = internalBOCs.find(b => b.id === selectedInternalBOC);
        requestData = {
          createFromLisaBackend: false,
          nodeIdFk: formData.nodeIdFk,
          officeMasterId: formData.officeMasterId,
          storeIdFk: formData.storeIdFk,
          remoteStoreIdFk: formData.remoteStoreIdFk,
          remoteBocIdFk: selectedInternalBOCData?.bocIdPk,
          remoteEnterpriseIdFk: formData.remoteEnterpriseIdFk,
          dealerCode: formData.dealerCode,
          officeCode: formData.officeCode,
          timeZone: formData.timeZone || null,
          companyId: selectedInternalBOCData?.bocId,
          n2CategoryId: formData.n2CategoryId ? Number(formData.n2CategoryId) : null,
        };
      }
      
      await api.post('/admin/office-mapping', requestData);
      await Promise.all([loadMappings(), loadLocalStores()]);
      setShowCreateForm(false);
      resetForm();
      showSuccess('Office mapping created');
    } catch (error) {
      console.error('Error creating office mapping:', error);
      setError(error.response?.data?.message || 'Failed to create office mapping');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateMapping = async (e) => {
    e.preventDefault();
    if (!editingMapping || submitting) return;

    setError('');
    setSubmitting(true);
    try {
      let updateData = { ...formData };

      // Add remoteBocIdFk based on selected BOC
      if (creationMode === 'lisa-backend' && selectedBoc) {
        const selectedBOCData = bocs.find(b => b.bocIdPk === selectedBoc);
        updateData.remoteBocIdFk = selectedBOCData?.bocIdPk;
      } else if (creationMode === 'internal-db' && selectedInternalBOC) {
        const selectedInternalBOCData = internalBOCs.find(b => b.id === selectedInternalBOC);
        updateData.remoteBocIdFk = selectedInternalBOCData?.bocIdPk;
      }

      await api.put(`/admin/office-mapping/${editingMapping.id}`, updateData);
      await Promise.all([loadMappings(), loadLocalStores()]);
      setEditingMapping(null);
      resetForm();
      showSuccess('Office mapping updated');
    } catch (error) {
      console.error('Error updating office mapping:', error);
      setError(error.response?.data?.message || 'Failed to update office mapping');
    } finally {
      setSubmitting(false);
    }
  };

  const mappingLabel = (id) => {
    const mapping = mappings.find((m) => m.id === id);
    if (!mapping) return 'this office mapping';
    const store = localStores.find((s) => s.id === mapping.storeIdFk);
    return store ? (store.name || store.storeId) : (mapping.officeCode || `#${id}`);
  };

  const handleDeleteMapping = async (id) => {
    const label = mappingLabel(id);
    const confirmed = await confirm({
      title: `Delete office mapping for "${label}"?`,
      message: `The mapping between "${label}" and Lisa Backend will be permanently deleted.`,
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;

    setError('');
    try {
      await api.delete(`/admin/office-mapping/${id}`);
      await loadMappings();
      showSuccess(`Office mapping for "${label}" deleted`);
    } catch (error) {
      console.error('Error deleting office mapping:', error);
      setError(error.response?.data?.message || 'Failed to delete office mapping');
    }
  };

  const handleToggleStatus = async (id) => {
    setError('');
    try {
      const mapping = mappings.find((m) => m.id === id);
      await api.post(`/admin/office-mapping/${id}/toggle-status`);
      await loadMappings();
      showSuccess(`Office mapping for "${mappingLabel(id)}" ${mapping?.isActive ? 'disabled' : 'enabled'}`);
    } catch (error) {
      console.error('Error toggling status:', error);
      setError(error.response?.data?.message || 'Failed to toggle status');
    }
  };

  const handleSyncMapping = async (id) => {
    setSyncingId(id);
    setError('');
    try {
      await api.post(`/admin/office-mapping/${id}/sync-from-lisa`);
      await loadMappings();
      showSuccess(`Office mapping for "${mappingLabel(id)}" synced from Lisa Backend`);
    } catch (error) {
      console.error('Error syncing office mapping:', error);
      setError(error.response?.data?.message || 'Failed to sync office mapping from Lisa Backend');
    } finally {
      setSyncingId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      nodeIdFk: '',
      officeMasterId: '',
      storeId: '',
      storeName: '',
      bocIdFk: '',
      enterpriseIdFk: '',
      remoteStoreIdFk: '',
      remoteEnterpriseIdFk: '',
      dealerCode: '',
      officeCode: '',
      timeZone: DEFAULT_TIME_ZONE,
      n2CategoryId: '',
    });
    setSelectedEnterprise(null);
    setSelectedBoc(null);
    setSelectedStore(null);
    setBocs([]);
    setStores([]);
    setSelectedInternalEnterprise(null);
    setSelectedInternalBOC(null);
    setSelectedInternalStore(null);
    setInternalBOCs([]);
    setInternalStores([]);
    setCreationMode('lisa-backend');
  };

  const startEdit = async (mapping) => {
    setEditLoading(true);
    setEditingMapping(mapping);
    setError('');
    setFormData({
      nodeIdFk: mapping.node?.id || '',
      officeMasterId: mapping.officeMasterId || '',
      storeIdFk: mapping.storeIdFk || '',
      remoteStoreIdFk: mapping.remoteStoreIdFk || '',
      remoteBocIdFk: mapping.remoteBocIdFk || '',
      remoteEnterpriseIdFk: mapping.remoteEnterpriseIdFk || '',
      dealerCode: mapping.dealerCode || '',
      officeCode: mapping.officeCode || '',
      timeZone: resolveTimeZone(mapping.timeZone),
      n2CategoryId: mapping.n2Category?.id?.toString() || '',
    });
    
    // Determine creation mode based on what data is available
    if (mapping.remoteEnterpriseIdFk || mapping.remoteStoreIdFk) {
      setCreationMode('lisa-backend');
      
      // Load enterprises for the node
      if (mapping.node?.id) {
        await loadEnterprises(mapping.node.id);
        
        // Set selected enterprise if available in mapping
        if (mapping.remoteEnterpriseIdFk) {
          setSelectedEnterprise(mapping.remoteEnterpriseIdFk);

          // Load BOCs directly and use response data
          const bocsResponse = await api.get(`/admin/office-mapping/lisa/enterprises/${mapping.remoteEnterpriseIdFk}/bocs?nodeId=${mapping.node.id}`);
          setBocs(bocsResponse.data);

          // Use remoteBocIdFk to directly select the BOC if available
          if (mapping.remoteBocIdFk) {
            const boc = bocsResponse.data.find(b => b.bocIdPk === mapping.remoteBocIdFk);
            if (boc) {
              setSelectedBoc(boc.bocIdPk);
              // Load stores for this specific BOC
              const storesResponse = await api.get(`/admin/office-mapping/lisa/bocs/${boc.bocIdPk}/stores?nodeId=${mapping.node.id}`);
              setStores(storesResponse.data);
              // Store selection will be handled by useEffect
            }
          } else if (mapping.remoteStoreIdFk && bocsResponse.data.length > 0) {
            // Fallback: iterate through BOCs to find the one containing the store
            for (const boc of bocsResponse.data) {
              const storesResponse = await api.get(`/admin/office-mapping/lisa/bocs/${boc.bocIdPk}/stores?nodeId=${mapping.node.id}`);
              setStores(storesResponse.data);
              const foundStore = storesResponse.data.find(s => String(s.storeIdPk) === String(mapping.remoteStoreIdFk));
              if (foundStore) {
                setSelectedBoc(boc.bocIdPk);
                // Store selection will be handled by useEffect
                break;
              }
            }
          }
        }
      }
    } else if (mapping.storeIdFk) {
      setCreationMode('internal-db');
      
      // Load internal data
      await loadInternalEnterprises();
      
      // Find the local store and set up the selection chain
      if (mapping.storeIdFk) {
        setSelectedInternalStore(mapping.storeIdFk);
        
        // Load internal BOCs to find the one containing this store
        await loadInternalBOCs();
        const bocWithStore = internalBOCs.find(boc => {
          const store = internalStores.find(s => s.id === mapping.storeIdFk);
          return store && store.bocIdFk === boc.id;
        });
        
        if (bocWithStore) {
          setSelectedInternalBOC(bocWithStore.id);
          await loadInternalStores(bocWithStore.id);
          
          // Find the enterprise containing this BOC
          const enterpriseWithBOC = internalEnterprises.find(ent => {
            const boc = internalBOCs.find(b => b.id === bocWithStore.id);
            return boc && boc.enterpriseIdFk === ent.id;
          });
          
          if (enterpriseWithBOC) {
            setSelectedInternalEnterprise(enterpriseWithBOC.id);
          }
        }
      }
    }
    
    setEditLoading(false);
    setShowCreateForm(true);
  };

  const cancelEdit = () => {
    setEditingMapping(null);
    setShowCreateForm(false);
    setError('');
    resetForm();
  };

  if (loading) return <AdminLoading message="Loading office mappings..." />;

  return (
    <div className="space-y-6">
      {toastElement}
      {confirmDialog}
      {editLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 shadow-xl flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <div className="text-lg font-medium text-gray-700">Loading mapping data...</div>
          </div>
        </div>
      )}

      <AdminPageHeader
        icon={MapPin}
        title="Office Mapping Management"
        subtitle="Manage mappings between local stores and Lisa Backend"
        rightContent={
          !showCreateForm && (
            <AdminAddButton
              onClick={() => {
                setShowCreateForm(true);
                setEditingMapping(null);
                setError('');
                resetForm();
              }}
              label="Add Office Mapping"
            />
          )
        }
      />

      {error && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 shadow-sm">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 flex items-center">
              {editingMapping ? (
                <>
                  <Edit className="h-6 w-6 mr-2 text-blue-600" />
                  Edit Office Mapping
                </>
              ) : (
                <>
                  <Plus className="h-6 w-6 mr-2 text-blue-600" />
                  Create New Office Mapping
                </>
              )}
            </h3>
            <button
              onClick={cancelEdit}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          

          <form onSubmit={editingMapping ? handleUpdateMapping : handleCreateMapping} className="space-y-4">
            {/* Node Selection */}
            <div className="border border-purple-200 rounded-xl p-5 bg-gradient-to-br from-purple-50 to-indigo-50 shadow-sm">
              <h4 className="text-sm font-semibold text-purple-900 mb-4 flex items-center">
                <Server className="h-4 w-4 mr-2" />
                Node Selection
              </h4>
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Node</label>
                <select
                  value={formData.nodeIdFk || ''}
                  onChange={(e) => {
                    const newNodeId = e.target.value ? Number(e.target.value) : null;
                    setFormData({ ...formData, nodeIdFk: newNodeId });
                    if (newNodeId) {
                      loadEnterprises(newNodeId);
                    }
                  }}
                  className="mt-1 block w-full pl-3 pr-10 py-2.5 text-base border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-lg shadow-sm transition-all duration-200 bg-white appearance-none"
                  required
                  disabled={submitting}
                >
                  <option value="">Select Node</option>
                  {nodes.map((node) => (
                    <option key={node.id} value={node.id}>
                      {node.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 mt-6">
                  <ChevronDown className="h-4 w-4" />
                </div>
              </div>
            </div>

            {!editingMapping && (
              <div className="flex items-center space-x-4 mb-6">
                <span className="text-sm font-medium text-gray-700">Creation Mode:</span>
                <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => {
                      setCreationMode('lisa-backend');
                      if (formData.nodeIdFk) loadEnterprises();
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      creationMode === 'lisa-backend'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    disabled={!formData.nodeIdFk}
                  >
                    Create from Lisa Backend
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCreationMode('internal-db');
                      loadInternalEnterprises();
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      creationMode === 'internal-db'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Use Internal DB
                  </button>
                </div>
              </div>
            )}

            {/* Lisa Backend Selection */}
            <div className="border border-blue-200 rounded-xl p-5 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-sm">
              <h4 className="text-sm font-semibold text-blue-900 mb-4 flex items-center">
                <Building className="h-4 w-4 mr-2" />
                Lisa Backend Selection
              </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Enterprise</label>
                    <select
                      value={selectedEnterprise || ''}
                      onChange={(e) => handleEnterpriseChange(e.target.value ? Number(e.target.value) : null)}
                      className="mt-1 block w-full pl-3 pr-10 py-2.5 text-base border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg shadow-sm transition-all duration-200 bg-white appearance-none"
                      required
                      disabled={submitting}
                    >
                      <option value="">Select Enterprise</option>
                      {enterprises.map((ent) => (
                        <option key={ent.enterpriseIdPk} value={ent.enterpriseIdPk}>
                          {ent.name}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 mt-6">
                      <ChevronDown className="h-4 w-4" />
                    </div>
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">BOC</label>
                    <select
                      value={selectedBoc || ''}
                      onChange={(e) => handleBocChange(e.target.value ? Number(e.target.value) : null)}
                      disabled={!selectedEnterprise || submitting}
                      className="mt-1 block w-full pl-3 pr-10 py-2.5 text-base border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg shadow-sm transition-all duration-200 bg-white appearance-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      required
                    >
                      <option value="">Select BOC</option>
                      {loadingBOCs ? (
                        <option disabled>Loading...</option>
                      ) : (
                        bocs.map((boc) => (
                          <option key={boc.bocIdPk} value={boc.bocIdPk}>
                          {boc.name ? `${boc.name} (${boc.bocId})` : boc.bocId}
                          </option>
                        ))
                      )}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 mt-6">
                      {loadingBOCs ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </div>
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Store</label>
                    <select
                      value={selectedStore || ''}
                      onChange={(e) => {
                        const store = stores.find(s => s.storeIdPk === Number(e.target.value));
                        handleStoreChange(e.target.value ? Number(e.target.value) : null, store);
                      }}
                      disabled={!selectedBoc || submitting}
                      className="mt-1 block w-full pl-3 pr-10 py-2.5 text-base border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg shadow-sm transition-all duration-200 bg-white appearance-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      required
                    >
                      <option value="">Select Store</option>
                      {loadingStores ? (
                        <option disabled>Loading...</option>
                      ) : (
                        stores.map((store) => (
                          <option key={store.storeIdPk} value={store.storeIdPk}>
                            {store.name} ({store.storeId})
                          </option>
                        ))
                      )}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 mt-6">
                      {loadingStores ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </div>
                  </div>
                </div>
              </div>

            {/* Internal DB Selection - only shown in internal-db mode */}
            {creationMode === 'internal-db' && !editingMapping && (
              <div className="border border-green-200 rounded-xl p-5 bg-gradient-to-br from-green-50 to-emerald-50 shadow-sm">
                <h4 className="text-sm font-semibold text-green-900 mb-4 flex items-center">
                  <Building className="h-4 w-4 mr-2" />
                  Internal DB Selection
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Enterprise</label>
                    <select
                      value={selectedInternalEnterprise || ''}
                      onChange={(e) => handleInternalEnterpriseChange(e.target.value ? Number(e.target.value) : null)}
                      className="mt-1 block w-full pl-3 pr-10 py-2.5 text-base border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-lg shadow-sm transition-all duration-200 bg-white appearance-none"
                      required
                      disabled={submitting}
                    >
                      <option value="">Select Enterprise</option>
                      {internalEnterprises.map((ent) => (
                        <option key={ent.id} value={ent.id}>
                          {ent.name}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 mt-6">
                      <ChevronDown className="h-4 w-4" />
                    </div>
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">BOC</label>
                    <select
                      value={selectedInternalBOC || ''}
                      onChange={(e) => handleInternalBOCChange(e.target.value ? Number(e.target.value) : null)}
                      disabled={!selectedInternalEnterprise || submitting}
                      className="mt-1 block w-full pl-3 pr-10 py-2.5 text-base border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-lg shadow-sm transition-all duration-200 bg-white appearance-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      required
                    >
                      <option value="">Select BOC</option>
                      {loadingInternalBOCs ? (
                        <option disabled>Loading...</option>
                      ) : (
                        internalBOCs.map((boc) => (
                          <option key={boc.id} value={boc.id}>
                            {boc.name ? `${boc.name} (${boc.bocId})` : boc.bocId}
                          </option>
                        ))
                      )}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 mt-6">
                      {loadingInternalBOCs ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </div>
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Store</label>
                    <select
                      value={selectedInternalStore || ''}
                      onChange={(e) => handleInternalStoreChange(e.target.value ? Number(e.target.value) : null)}
                      disabled={!selectedInternalBOC || submitting}
                      className="mt-1 block w-full pl-3 pr-10 py-2.5 text-base border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-lg shadow-sm transition-all duration-200 bg-white appearance-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      required
                    >
                      <option value="">Select Store</option>
                      {loadingInternalStores ? (
                        <option disabled>Loading...</option>
                      ) : (
                        internalStores.map((store) => (
                          <option key={store.id} value={store.id}>
                            {store.name ? `${store.name} (${store.storeId})` : store.storeId}
                          </option>
                        ))
                      )}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 mt-6">
                      {loadingInternalStores ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Manual Office Code Input */}
            <div className="border border-orange-200 rounded-xl p-5 bg-gradient-to-br from-orange-50 to-amber-50 shadow-sm">
              <h4 className="text-sm font-semibold text-orange-900 mb-4 flex items-center">
                <MapPin className="h-4 w-4 mr-2" />
                Office Code (Manual Entry)
              </h4>
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">Office Code</label>
                <input
                  type="text"
                  value={formData.officeCode || ''}
                  onChange={(e) => setFormData({ ...formData, officeCode: e.target.value })}
                  className="mt-1 block w-full pl-3 pr-10 py-2.5 text-base border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 sm:text-sm rounded-lg shadow-sm transition-all duration-200 bg-white"
                  placeholder="Enter office code manually"
                />
              </div>
            </div>

            {/* Time Zone Selection */}
            <div className="border border-teal-200 rounded-xl p-5 bg-gradient-to-br from-teal-50 to-cyan-50 shadow-sm">
              <h4 className="text-sm font-semibold text-teal-900 mb-4 flex items-center">
                <Clock className="h-4 w-4 mr-2" />
                Time Zone
              </h4>
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Time Zone</label>
                <select
                  value={formData.timeZone || ''}
                  onChange={(e) => setFormData({ ...formData, timeZone: e.target.value })}
                  className="mt-1 block w-full pl-3 pr-10 py-2.5 text-base border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 sm:text-sm rounded-lg shadow-sm transition-all duration-200 bg-white appearance-none"
                >
                  <option value="">Select Time Zone</option>
                  {TIME_ZONE_OPTIONS.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 mt-6">
                  <ChevronDown className="h-4 w-4" />
                </div>
              </div>
            </div>

            {/* N2 Category Selection */}
            <div className="border border-indigo-200 rounded-xl p-5 bg-gradient-to-br from-indigo-50 to-purple-50 shadow-sm">
              <h4 className="text-sm font-semibold text-indigo-900 mb-4 flex items-center">
                <Tag className="h-4 w-4 mr-2" />
                N2 Category
              </h4>
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Category</label>
                <select
                  value={formData.n2CategoryId || ''}
                  onChange={(e) => setFormData({ ...formData, n2CategoryId: e.target.value })}
                  className="mt-1 block w-full pl-3 pr-10 py-2.5 text-base border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-lg shadow-sm transition-all duration-200 bg-white appearance-none"
                >
                  <option value="">No Category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 mt-6">
                  <ChevronDown className="h-4 w-4" />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={cancelEdit}
                disabled={submitting}
                className="px-5 py-2.5 border border-gray-300 rounded-xl shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="h-4 w-4 inline mr-2" />
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2.5 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {editingMapping ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 inline mr-2" />
                    {editingMapping ? 'Update Mapping' : 'Create Mapping'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Mappings Table */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <MapPin className="h-5 w-5 mr-2 text-gray-600" />
            Office Mappings ({mappings.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Store</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Node</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Office Code</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Time Zone</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Remote Office ID</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Remote Store ID</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Remote Enterprise ID</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mappings.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                    <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No office mappings found. Create one to get started.</p>
                  </td>
                </tr>
              ) : (
                mappings.map((mapping) => (
                  <tr key={mapping.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(() => {
                        const store = localStores.find(s => s.id === mapping.storeIdFk);
                        if (!store) return mapping.storeIdFk || '-';
                        return (
                          <>
                            <div className="font-medium">{store.name || store.storeId}</div>
                            {store.name && <div className="text-xs text-gray-400">{store.storeId}</div>}
                          </>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {mapping.node ? (
                        <div className="flex items-center">
                          <Server className="h-4 w-4 mr-2 text-gray-500" />
                          <span className="font-medium">{mapping.node.name}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{mapping.officeCode || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(() => {
                        const tzColor = getTimeZoneColor(mapping.timeZone);
                        return (
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium border rounded-md ${tzColor.bg} ${tzColor.text} ${tzColor.border}`}>
                            {formatTimeZoneLabel(mapping.timeZone)}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{mapping.officeMasterId || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{mapping.remoteStoreIdFk || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{mapping.remoteEnterpriseIdFk || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                        mapping.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        <span className={`w-2 h-2 rounded-full mr-2 ${
                          mapping.isActive ? 'bg-green-500' : 'bg-red-500'
                        }`}></span>
                        {mapping.isActive ? 'Active' : 'Inactive'}
                      </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <AdminRowActions align="start">
                    <AdminActionButton variant="edit" onClick={() => startEdit(mapping)} />
                    <AdminActionButton variant={mapping.isActive ? 'deactivate' : 'activate'} onClick={() => handleToggleStatus(mapping.id)} />
                    <AdminActionButton variant="info" icon={RefreshCw} label="Sync" loading={syncingId === mapping.id} onClick={() => handleSyncMapping(mapping.id)} />
                    <AdminActionButton variant="delete" onClick={() => handleDeleteMapping(mapping.id)} />
                  </AdminRowActions>
                </td>
              </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OfficeMappingManagement;
